import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  description?: string;
  loading?: boolean;
  trend?: {
    value: number;
    positive: boolean;
  };
}

export function StatsCard({ title, value, icon: Icon, description, loading = false, trend }: StatsCardProps) {
  return (
    <Card className="border-border/50 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] bg-gradient-to-br from-background to-muted/10">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {loading ? (
          <div className="space-y-2">
            <div className="h-8 w-20 bg-muted/60 animate-pulse rounded" />
            <div className="h-3 w-24 bg-muted/40 animate-pulse rounded" />
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                {value}
              </div>
              {trend && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    trend.positive
                      ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/20'
                      : 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/20'
                  }`}
                >
                  {trend.positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(trend.value)}%
                </div>
              )}
            </div>
            {description && <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
